import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate, useLocation, Outlet } from 'react-router-dom';
import { listProjects } from '../services/projects';
import { DEFAULT_BRANCH, parseProjectPath } from '../utils/projectPath';

// The active project + branch come ONLY from the URL path
// (/p/:projectSlug/b/:branch/...). This provider exposes them + the project
// list, and switches project/branch by NAVIGATING (never by storage or a token
// re-sign). It also guards the route: an unknown project slug redirects to the
// first available project.
const ProjectContext = createContext(null);

export const useProject = () => {
  const ctx = useContext(ProjectContext);
  if (!ctx) throw new Error('useProject must be used within a ProjectProvider');
  return ctx;
};

// Provider-OPTIONAL variant for surfaces that deliberately live outside the
// provider (the chrome-free share page): returns null instead of throwing, so
// the caller can fall back to parsing the URL.
export const useProjectMaybe = () => useContext(ProjectContext);

export const ProjectProvider = () => {
  const { projectSlug, branch } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [projects, setProjects] = useState(null); // null = loading
  const [loaded, setLoaded] = useState(false);     // true ONLY after a successful fetch
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      // Retry once on transient failure (cold pod / token refresh) before giving
      // up — a failed fetch must NEVER be read as "this project doesn't exist".
      for (let attempt = 0; attempt < 2; attempt++) {
        try {
          const list = await listProjects();
          if (!alive) return;
          setProjects(list || []);
          setLoaded(true);       // only a genuine success flips this
          setError(null);
          return;
        } catch (e) {
          if (!alive) return;
          setError(e?.message || 'Failed to load projects');
          if (attempt === 0) { await new Promise((r) => setTimeout(r, 800)); }
        }
      }
      // Both attempts failed: keep projects null + loaded false so the guard
      // below stays inert and never bounces a (possibly valid) project.
    })();
    return () => { alive = false; };
  }, []);

  // Guard: redirect to the first available project ONLY when we have a confirmed,
  // non-empty project list AND the URL slug is genuinely not in it. We require
  // `loaded` (a successful fetch) so a slow/failed load can never wrongly bounce a
  // valid project like potential_customers to the default one.
  useEffect(() => {
    if (!loaded) return;                           // no confirmed list yet
    if (!projectSlug) return;                      // handled by RedirectToFirstProject
    if (!projects || projects.length === 0) {
      // CONFIRMED empty list but the URL still pins a project (e.g. a stale
      // slug left in the browser from another account): staying here strands
      // the user in a context where every /api call 403s. Send them to the
      // first-run landing instead.
      navigate('/', { replace: true });
      return;
    }
    const known = projects.some((p) => p.slug === projectSlug);
    if (known) return;
    const firstRow = projects[0];
    const first = firstRow?.slug;
    if (!first || first === projectSlug) return;
    const { rest } = parseProjectPath(location.pathname);
    // Carrying `rest` across keeps the user on the same sub-page in the project
    // we can actually resolve — EXCEPT when that project is share-only, where
    // every sub-page but Apps 403s. Landing them on Apps avoids handing them a
    // route Layout's guard would immediately redirect away from.
    const home = firstRow.shared_surface_only ? 'apps' : (rest || 'dashboard');
    navigate(`/p/${encodeURIComponent(first)}/b/${DEFAULT_BRANCH}/${home}`, { replace: true });
  }, [loaded, projects, projectSlug, location.pathname, navigate]);

  const value = useMemo(() => {
    const currentBranch = branch || DEFAULT_BRANCH;
    // Switch project: navigate to the same sub-page under the new project on main.
    const setProject = (slug) => {
      const { rest } = parseProjectPath(location.pathname);
      navigate(`/p/${encodeURIComponent(slug)}/b/${DEFAULT_BRANCH}/${rest || 'dashboard'}`);
    };
    // Switch branch: keep project + sub-page, change the branch segment.
    const setBranch = (b) => {
      const { rest } = parseProjectPath(location.pathname);
      navigate(`/p/${encodeURIComponent(projectSlug)}/b/${encodeURIComponent(b)}/${rest || 'dashboard'}`);
    };
    const currentProject = (projects || []).find((p) => p.slug === projectSlug) || null;
    return {
      projectSlug: projectSlug || null,
      branch: currentBranch,
      projects: projects || [],
      projectsLoading: !loaded,
      currentProject,
      // How the caller reaches THIS project: 'owner' | 'shared' | 'organization'
      // (set by GET /api/projects). `sharedSurfaceOnly` is true when the ONLY
      // grant is on a single app/agent — the project itself is closed to them,
      // so every project-scoped page would 403. The nav uses it to show just
      // the surfaces they can actually open. Defaults to false while the list
      // is still loading, so the full nav never flickers away for a normal user.
      projectAccess: currentProject?.access || null,
      sharedSurfaceOnly: !!currentProject?.shared_surface_only,
      setProject,
      setBranch,
      error,
    };
  }, [projectSlug, branch, projects, loaded, location.pathname, navigate, error]);

  return (
    <ProjectContext.Provider value={value}>
      <Outlet />
    </ProjectContext.Provider>
  );
};
